import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../utils/sentryHelpers';
import { pickSourceText } from '../../../utils/slugify';
import {
  extractSlugLookupItems,
  findSlugOwner,
  resolveRequiredSlug,
  slugHistoryMutationInput,
} from '../../../utils/slugInput';
import {
  DEFAULT_MEDIA_STATUS,
  buildMediaItemInput,
  compareMediaSeries,
  nowIsoMinute,
  setLocalized,
  validateMediaItem,
} from '../../../utils/mediathek';
import {
  CREATE_MEDIA_ITEM,
  DELETE_MEDIA_ITEM,
  GET_COMPANY_NAME,
  GET_MEDIA_ITEM,
  LIST_MEDIA_SERIES_OPTIONS,
  MEDIA_ITEMS_BY_SLUG,
  SEARCH_COMPANIES,
  UPDATE_MEDIA_ITEM,
  fetchAllPages,
} from '../mediathekGraphql';

const client = generateClient();
const COMPONENT = 'useMediaItemData';

const STRING_FIELDS = [
  'topic', 'guest', 'guestEmail', 'guestPhone', 'host', 'speaker',
  'companyName', 'companyUrl', 'companyId', 'externalVideoUrl', 'spotifyUrl', 'podigeeUrl',
];

const emptyItem = (mediaSeriesId) => ({
  mediaSeriesId: mediaSeriesId || '',
  title: [],
  slug: '',
  slugHistory: [],
  excerpt: [],
  content: [],
  episodeNumber: '',
  publishedAt: nowIsoMinute(),
  isHero: false,
  status: DEFAULT_MEDIA_STATUS,
  sortKey: '',
  image: null,
  audio: null,
  video: null,
  ...Object.fromEntries(STRING_FIELDS.map((field) => [field, ''])),
});

/** Loaded record -> form state (inputs need strings instead of null). */
function toFormState(item) {
  const form = {
    ...item,
    title: item.title || [],
    slug: item.slug || '',
    slugHistory: item.slugHistory || [],
    excerpt: item.excerpt || [],
    content: item.content || [],
    episodeNumber: item.episodeNumber === null || item.episodeNumber === undefined ? '' : String(item.episodeNumber),
    sortKey: item.sortKey === null || item.sortKey === undefined ? '' : String(item.sortKey),
    isHero: Boolean(item.isHero),
    status: item.status || DEFAULT_MEDIA_STATUS,
  };
  for (const field of STRING_FIELDS) form[field] = item[field] || '';
  return form;
}

function reportError(error, operation, context) {
  console.error(`Mediathek item: ${operation} failed`, error);
  captureError(error, { feature: 'mediathek', operation, context, tags: { component: COMPONENT } });
}

/**
 * Data + actions of the item edit page (create when itemId is empty).
 * @param {string|undefined} itemId
 * @param {string|undefined} routeSeriesId series preselected by the create route
 */
export default function useMediaItemData(itemId, routeSeriesId) {
  const isUpdate = Boolean(itemId);
  const [item, setItem] = useState(() => emptyItem(routeSeriesId));
  // As loaded: slug/slugHistory are maintained by this form; moving the item to another
  // series re-checks the slug within the new series
  const [loadedSlug, setLoadedSlug] = useState(null);
  const [loadedHistory, setLoadedHistory] = useState([]);
  const [loadedSeriesId, setLoadedSeriesId] = useState(null);
  const [seriesList, setSeriesList] = useState([]);
  const [linkedCompany, setLinkedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(isUpdate);
  const [isLoadingSeries, setIsLoadingSeries] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loadVersion, setLoadVersion] = useState(0);

  const fetchItem = useCallback(async () => {
    if (!itemId) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await client.graphql({ query: GET_MEDIA_ITEM, variables: { id: itemId } });
      const loaded = response?.data?.getMediaItem;
      if (!loaded) {
        setLoadError('The item was not found');
        return;
      }
      setItem(toFormState(loaded));
      setLoadedSlug(loaded.slug ?? null);
      setLoadedHistory(loaded.slugHistory || []);
      setLoadedSeriesId(loaded.mediaSeriesId ?? null);
      setLoadVersion((v) => v + 1);
    } catch (error) {
      reportError(error, 'fetch-item', { itemId });
      setLoadError('The item could not be loaded');
    } finally {
      setIsLoading(false);
    }
  }, [itemId]);

  const fetchSeriesList = useCallback(async () => {
    setIsLoadingSeries(true);
    try {
      const all = await fetchAllPages(client, LIST_MEDIA_SERIES_OPTIONS, {}, 'listMediaSeries');
      setSeriesList(all.sort(compareMediaSeries));
    } catch (error) {
      reportError(error, 'fetch-series-options', {});
      setErrors((prev) => [...prev, 'The series could not be loaded']);
    } finally {
      setIsLoadingSeries(false);
    }
  }, []);

  useEffect(() => {
    fetchSeriesList();
  }, [fetchSeriesList]);

  useEffect(() => {
    if (itemId) {
      fetchItem();
    } else {
      setItem(emptyItem(routeSeriesId));
      setLoadedSlug(null);
      setLoadedHistory([]);
      setLoadedSeriesId(null);
      setIsLoading(false);
    }
  }, [itemId, routeSeriesId, fetchItem]);

  // Name of the linked company (companyId is optional; companyName stays the free-text fallback)
  useEffect(() => {
    const companyId = item.companyId;
    if (!companyId) {
      setLinkedCompany(null);
      return undefined;
    }
    if (linkedCompany?.id === companyId) return undefined;
    let cancelled = false;
    client.graphql({ query: GET_COMPANY_NAME, variables: { id: companyId } })
      .then((response) => {
        if (cancelled) return;
        const company = response?.data?.getCompany;
        setLinkedCompany({ id: companyId, name: company?.name || companyId, missing: !company });
      })
      .catch((error) => {
        if (cancelled) return;
        reportError(error, 'fetch-linked-company', { companyId });
        setLinkedCompany({ id: companyId, name: companyId, missing: false });
      });
    return () => {
      cancelled = true;
    };
  }, [item.companyId, linkedCompany?.id]);

  const setField = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setLocalizedField = useCallback((field, language, text) => {
    setItem((prev) => ({ ...prev, [field]: setLocalized(prev[field], language, text) }));
  }, []);

  const selectCompany = useCallback((company) => {
    if (!company?.id) return;
    setLinkedCompany({ id: company.id, name: company.name || company.id, missing: false });
    setItem((prev) => ({
      ...prev,
      companyId: company.id,
      // keep an existing free-text name, otherwise take over the company name
      companyName: prev.companyName && prev.companyName.trim() ? prev.companyName : (company.name || ''),
    }));
  }, []);

  const clearCompany = useCallback(() => {
    setLinkedCompany(null);
    setItem((prev) => ({ ...prev, companyId: '' }));
  }, []);

  /** Active companies whose name starts with the query ([] on errors, which are reported). */
  const searchCompanies = useCallback(async (query) => {
    const text = typeof query === 'string' ? query.trim() : '';
    if (text.length < 2) return [];
    try {
      const response = await client.graphql({
        query: SEARCH_COMPANIES,
        variables: {
          filter: { or: [{ name: { matchPhrasePrefix: text } }], status: { eq: 'active' } },
          limit: 8,
        },
      });
      return (response?.data?.searchCompanies?.items || []).filter(Boolean);
    } catch (error) {
      reportError(error, 'search-companies', { query: text });
      return [];
    }
  }, []);

  const seriesById = useMemo(() => Object.fromEntries(seriesList.map((s) => [s.id, s])), [seriesList]);
  const currentSeries = seriesById[item.mediaSeriesId] || null;

  /** true when another item of the same series already uses the slug (throws when the lookup fails). */
  const isSlugTaken = useCallback(async (slug, mediaSeriesId) => {
    let lookupItems;
    try {
      const response = await client.graphql({ query: MEDIA_ITEMS_BY_SLUG, variables: { slug, limit: 50 } });
      lookupItems = extractSlugLookupItems(response, 'mediaItem');
    } catch (error) {
      // A rejected GraphQLResult may still carry the items (partial data); otherwise save() reports it
      lookupItems = extractSlugLookupItems(error, 'mediaItem');
      if (!lookupItems) throw error;
      reportError(error, 'slug-lookup-partial', { slug, mediaSeriesId });
    }
    return findSlugOwner(lookupItems || [], itemId, 'mediaItem', mediaSeriesId) !== null;
  }, [itemId]);

  /**
   * Validates and saves. Returns { id, mediaSeriesId, slug } on success, null
   * otherwise (errors are in `errors`).
   */
  const save = useCallback(async () => {
    const validation = validateMediaItem(item);
    if (!validation.length && seriesList.length > 0 && !seriesById[item.mediaSeriesId]) {
      validation.push('The selected series does not exist');
    }
    if (validation.length) {
      setErrors(validation);
      return null;
    }
    setErrors([]);
    setIsSaving(true);
    try {
      const movedToOtherSeries = isUpdate && loadedSeriesId && loadedSeriesId !== item.mediaSeriesId;
      const slugResult = await resolveRequiredSlug(item.slug, {
        loadedSlug,
        sourceText: pickSourceText(item.title),
        fallback: 'episode',
        recheck: Boolean(movedToOtherSeries),
        isTaken: (slug) => isSlugTaken(slug, item.mediaSeriesId),
      });
      if (slugResult.conflict) {
        setErrors(['This slug is already used by another item of this series']);
        return null;
      }
      const input = {
        ...buildMediaItemInput({ ...item, id: itemId }, { isUpdate }),
        slug: slugResult.slug,
        ...(isUpdate ? slugHistoryMutationInput(slugResult.slug, loadedSlug, loadedHistory) : {}),
      };
      const response = await client.graphql({
        query: isUpdate ? UPDATE_MEDIA_ITEM : CREATE_MEDIA_ITEM,
        variables: { input },
      });
      const saved = isUpdate ? response?.data?.updateMediaItem : response?.data?.createMediaItem;
      if (!saved?.id) throw new Error('The item could not be saved');
      const savedSlug = saved.slug ?? slugResult.slug;
      setItem((prev) => ({ ...prev, id: saved.id, slug: savedSlug, slugHistory: saved.slugHistory || [] }));
      setLoadedSlug(savedSlug);
      setLoadedHistory(saved.slugHistory || []);
      setLoadedSeriesId(saved.mediaSeriesId || item.mediaSeriesId);
      return { id: saved.id, mediaSeriesId: saved.mediaSeriesId || item.mediaSeriesId, slug: savedSlug };
    } catch (error) {
      reportError(error, isUpdate ? 'update-item' : 'create-item', { itemId, mediaSeriesId: item.mediaSeriesId });
      setErrors([error?.errors?.[0]?.message || error?.message || 'The item could not be saved']);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [item, itemId, isUpdate, seriesList.length, seriesById, loadedSeriesId, loadedSlug, loadedHistory, isSlugTaken]);

  /** Deletes the item (the audio/video objects stay in S3). Returns true on success. */
  const deleteItem = useCallback(async () => {
    if (!itemId) return false;
    setErrors([]);
    try {
      await client.graphql({ query: DELETE_MEDIA_ITEM, variables: { input: { id: itemId } } });
      return true;
    } catch (error) {
      reportError(error, 'delete-item', { itemId });
      setErrors([error?.errors?.[0]?.message || error?.message || 'The item could not be deleted']);
      return false;
    }
  }, [itemId]);

  return {
    item,
    setItem,
    setField,
    setLocalizedField,
    loadedSlug,
    loadedSeriesId,
    seriesList,
    seriesById,
    currentSeries,
    linkedCompany,
    selectCompany,
    clearCompany,
    searchCompanies,
    isLoading,
    isLoadingSeries,
    loadError,
    isSaving,
    errors,
    setErrors,
    loadVersion,
    save,
    deleteItem,
  };
}
